import { Suspense, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Loading from "../../../../common/components/ui/loading/Loading";
import { formatDateAndTime } from "../../../../common/helpers/date.helpers";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { getCompanyId } from "../../../../common/services/company.api";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";

import GenericTable from "../../../../common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import TableFooter from "../../../../common/components/ui/table/TableFooter";

import {
  IIndicators,
  INDICATORS_STATUS,
  IndicatorType,
} from "../../../../types/Indicators.type";
import PendenciesIndicators from "../PendenciesIndicators";
import ModalCreateIndicator from "../modals/CreateIndicator";
import ModalEditIndicator from "../modals/EditIndicator";
import ModalEditAmbassador from "../../../ambassador/modals/EditAmbassador";

import { getIndicators } from "../../services/indicator.api";
import { getAmbassadorByEmail } from "../../../../common/services/ambassador";
import { useCompanyStore } from "@/common/states/store/Company";
import { getCompanySigla } from "@/common/helpers/company.helpers";

const STATUS_LABELS = {
  [INDICATORS_STATUS.ambassador]: "Embaixador",
  [INDICATORS_STATUS.rentalpoint]: "Ponto de Locação",
  [INDICATORS_STATUS.indicator]: "Indicador",
};

function getStatusLabels(status: INDICATORS_STATUS) {
  return STATUS_LABELS[status] || status;
}

const EMPTY_ACTION = { eventName: "", data: undefined };

const headers = {
  companyId: "Cidade",
  name: "Nome",
  pendencies: "Pendências",
  type: "Tipo",
  updatedAt: "Atualizado em",
  createdAt: "Criado em",
};

function TableIndicators() {
  const { auth } = useContext(AuthContext);
  const companyId = getCompanyId();
  const company = useCompanyStore();
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [indicators, setIndicators] = useState<IIndicators[]>([]);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });
  const [dataAmbassador, setDataAmbassador] = useState<IIndicators>();
  const [isAmbassador, setAmbassador] = useState(false);
  const [pendingDataAmbassador, setPendingDataAmbassador] = useState(false);

  useEffect(() => {
    async function loadIndicators(filters, page) {
      setIsLoading(true);
      setIndicators([]);

      const result = await getIndicators({
        ...filters,
        page,
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }
      setIndicators(result.data);
      setIsLoading(false);
    }

    loadIndicators(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, notifyError, companyId, company]);

  useEffect(() => {
    if (!auth || !auth.user || !auth.user.roles) {
      setAmbassador(false);
      return;
    }

    const isAmbassador = auth.user.roles.includes("ambassador");
    setAmbassador(isAmbassador);

    if (isAmbassador) {
      const email = auth.user.email || "";

      const getDataAmbassador = async (email) => {
        setIsLoading(true);
        try {
          const result = await getAmbassadorByEmail(email);
          if (!result.ok) {
            notifyError(result.message || "Unknown error");
            return;
          }

          const data = result.data?.[0];
          if (data?.cpf === "") {
            setPendingDataAmbassador(true);
          }
          setDataAmbassador(data);
        } catch (error) {
          notifyError("Error fetching ambassador data");
        } finally {
          setIsLoading(false);
        }
      };

      getDataAmbassador(email);
    }
  }, [auth, notifyError]);

  const actions = [
    {
      label: "Editar",
      action: (data) =>
        setAction({ eventName: "edit", data: (data as IndicatorType)._id }),
      permission: "edit:indicators",
    },
    {
      label: "Ativar",
      action: (data) => setAction({ eventName: "activate", data: data }),
      permission: "edit:indicators",
    },
    {
      label: "Desativar",
      action: (data) => setAction({ eventName: "deactivate", data: data }),
      permission: "edit:indicators",
    },
    {
      label: "Bloquear",
      action: (data) => setAction({ eventName: "block", data: data }),
      permission: "edit:indicators",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => setAction(EMPTY_ACTION);

  const updatedAmbassador = () => setPendingDataAmbassador(false);

  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };

  function onRenterCreated(renterId: string) {
    handleSearch(filters);
    setAction({
      eventName: "edit",
      data: renterId,
    });
  }

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <>
      {isAmbassador && dataAmbassador && pendingDataAmbassador && (
        <ModalEditAmbassador
          onClose={updatedAmbassador}
          data={dataAmbassador}
        />
      )}
      <Suspense fallback={<Loading />}>
        <div className="table-section">
          <TableFilters
            dashboardHeader="Embaixadores cadastrados"
            placeholder="Digite o nome, o e-mail, o CPF ou a CNH."
            statusOptions={STATUS_LABELS}
            newItemLabel="Novo Embaixador"
            onNewItemClick={() =>
              setAction({ eventName: "create", data: undefined })
            }
            onSearch={handleFilterChange}
            entity="indicators"
            tableData={indicators}
          />
          <GenericTable
            headers={headers}
            data={indicators}
            actions={actions}
            setFilters={({ orderBy }) => {
              setFilters((prevFilters) => ({
                ...prevFilters,
                orderBy,
              }));
            }}
            renders={
              {
                companyId: (companyId) => {
                  return getCompanySigla(companyId);
                },
                name: (name, indicator) => (
                  <a
                    href={`/indicadores/${(indicator as IIndicators)._id}`}
                    onClick={(e) => {
                      e.preventDefault(); // Para evitar o comportamento padrão do link
                      navigate(
                        `/indicadores/${(indicator as IIndicators)._id}`
                      ); // Navegação na mesma página
                    }}
                    target="_blank" // Abre em uma nova aba
                    rel="noopener noreferrer" // Melhoria de segurança
                  >
                    {name as string}
                  </a>
                ),
                pendencies: (_, indicator) => (
                  <PendenciesIndicators
                    indicator={indicator as IndicatorType}
                  />
                ),
                updatedAt: formatDateAndTime,
                createdAt: formatDateAndTime,
                type: getStatusLabels,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
            entity="indicators"
          />

          {isLoading && <Loading />}

          <TableFooter
            page={page || 1}
            totalItems={data.totalItems || 0}
            itemsPerPage={+(filters.itemsPerPage ?? 10)}
            goToPage={setPage}
          />

          {isAction("create") && (
            <ModalCreateIndicator
              onClosed={cleanActionData}
              onSave={onRenterCreated}
            />
          )}
          {isAction("edit") && (
            <ModalEditIndicator
              indicatorId={action.data as string}
              onClose={cleanActionData}
            />
          )}
        </div>
      </Suspense>
    </>
  );
}

export default TableIndicators;
